import { useState } from 'react';
import type { ReactNode } from 'react';
import { Alert, Box, Button, Chip, Menu, MenuItem, Stack, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import type { AssetReconciliationResult } from './sales-force-asset-reconciliation';
import { ReconciliationLink, ReconciliationLinks, salesforceRecordUrl, webkuaContractUrl, webkuaProductListUrl } from './reconciliation-links';
import { ReconciliationDetailsLayout, ReconciliationSourcePanel, ReconciliationSourcePanels } from './reconciliation-details-layout';

export type AssetSourceRow = AssetReconciliationResult['rows'][number];
export const assetQuickFilters = [
  'afterRenewal', 'recent', 'otherContract', 'contractDistribution', 'discountMismatch', 'pending', 'unresolved', 'sfZeroWkPositive',
] as const;
export type AssetQuickFilter = typeof assetQuickFilters[number];

export function matchesAssetFilter(row: AssetSourceRow, filter: AssetQuickFilter, now: Date): boolean {
  switch (filter) {
    case 'afterRenewal': return row.usedAfterRenewalDate === true;
    case 'recent': {
      if (!row.lastUsedDate2026) return false;
      const today = Date.UTC(now.getFullYear(), now.getMonth(), now.getDate());
      const used = Date.parse(`${row.lastUsedDate2026.slice(0, 10)}T00:00:00Z`);
      return Number.isFinite(used) && used <= today && used >= today - 30 * 86400000;
    }
    case 'otherContract': return row.kind === 'MissingInWebkua' && (row.otherWebkuaContracts?.length ?? 0) > 0;
    case 'discountMismatch': return row.kind === 'DiscountMismatch';
    case 'contractDistribution': return row.companyItemComparison?.status === 'ContractDistributionMismatch';
    case 'pending': return (row.pendingOrders?.length ?? 0) > 0;
    case 'unresolved': return [
      'AmbiguousContract', 'UnmappedProduct', 'MissingKey', 'UnclassifiedAsset', 'UnmappedWebkuaItem', 'ComparisonBlocked',
      'DiscountComparisonBlocked',
    ].includes(row.kind) || row.companyItemComparison?.status === 'ComparisonBlocked';
    case 'sfZeroWkPositive': return row.salesforceActiveQuantity === 0 && (row.webkuaActiveQuantity ?? 0) > 0;
  }
}

export function assetStatus(row: AssetSourceRow, t: TFunction): string {
  const key = row.kind === 'ActivationMismatch'
    ? row.salesforceActiveQuantity === 0 ? 'wkOnlyActive' : 'sfOnlyActive'
    : row.kind === 'UnmappedProduct' ? row.productMatchIssue ?? row.kind : row.kind;
  return t(`salesForceAssetReconciliation.compactStatus.${key}`, { defaultValue: row.kind });
}

export function AssetQuantityLink({ row, system }: { row: AssetSourceRow; system: 'SF' | 'WK' }) {
  const { t } = useTranslation();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const value = system === 'SF' ? row.salesforceActiveQuantity : row.webkuaActiveQuantity;
  if (value === null) return <>-</>;
  const label = String(value);
  if (system === 'WK') {
    return <ReconciliationLink label={label} href={row.companyNumber ? webkuaProductListUrl(row.companyNumber) : null} />;
  }
  const ids = row.salesforceAssetIds;
  if (ids.length <= 1) {
    return <ReconciliationLink label={label} href={ids[0] ? salesforceRecordUrl('Asset', ids[0]) : null} />;
  }
  return <>
    <Button size="small" title={String(t('salesForceAssetReconciliation.chooseAssets', { returnNull: false }))}
      sx={{ minWidth: 0, p: 0, font: 'inherit', lineHeight: 'inherit', textDecoration: 'underline' }}
      onClick={(event) => { event.stopPropagation(); setAnchor(event.currentTarget); }}>{label}</Button>
    <Menu anchorEl={anchor} open={anchor !== null} onClose={() => setAnchor(null)}>
      {ids.map((id, index) => {
        const asset = row.salesforceAssets?.find((source) => source.id === id);
        return <MenuItem key={id} component="a" href={salesforceRecordUrl('Asset', id)}
        target="_blank" rel="noopener noreferrer" onClick={(event) => { event.stopPropagation(); setAnchor(null); }}>
          {t('salesForceAssetReconciliation.assetChoice', {
            number: index + 1, name: asset?.name || asset?.productCode || 'Asset',
            status: asset?.status || '-', quantity: asset?.quantity ?? '-', date: sourceDate(asset?.usageEndDate),
          })}
        </MenuItem>;
      })}
    </Menu>
  </>;
}

export function AssetContractLinks({ row }: { row: Pick<AssetSourceRow,
  'companyNumber' | 'salesforceContractIds' | 'salesforceContractNumber' | 'webkuaContractNumbers' | 'webkuaContractNumber'> }) {
  const { t } = useTranslation();
  const numbers = row.webkuaContractNumbers?.length ? row.webkuaContractNumbers
    : row.webkuaContractNumber ? [row.webkuaContractNumber] : [];
  return <Box sx={{ whiteSpace: 'nowrap' }}>
    SF: {row.salesforceContractIds?.length
      ? <ReconciliationLinks links={row.salesforceContractIds.map((id) => ({
        label: row.salesforceContractNumber || String(t('salesForceAssetReconciliation.contracts', { returnNull: false })),
        href: salesforceRecordUrl('Contract', id),
      }))} />
      : row.salesforceContractNumber ?? '-'}{' / WK: '}<ReconciliationLinks links={numbers.map((number) => ({
      label: number, href: row.companyNumber ? webkuaContractUrl(row.companyNumber, number) : null,
    }))} />{numbers.length === 0 && '-'}
  </Box>;
}

const sourceDate = (value: string | null | undefined) => value?.slice(0, 10) ?? '-';

export function salesforceUsageDates(row: AssetSourceRow): string {
  return [...new Set((row.salesforceAssets ?? []).map((asset) => sourceDate(asset.usageEndDate)))].sort().join(', ') || '-';
}

export function AssetItemCodes({ row }: { row: AssetSourceRow }) {
  return <Box sx={{ lineHeight: 1.35 }}>
    <div>SF: {row.salesforceProductCode || '-'}</div>
    <div>WK: {row.itemId || '-'}</div>
    {row.itemName && <Typography variant="caption" component="div">{row.itemName}</Typography>}
  </Box>;
}

export function AssetRenewalDates({ row }: { row: AssetSourceRow }) {
  const { t } = useTranslation();
  const dates = [...new Set((row.webkuaContractDates ?? []).map((contract) => sourceDate(contract.nextInvoicingDate)))];
  return <Box sx={{ lineHeight: 1.35 }} title={String(t('salesForceAssetReconciliation.dateSources', { returnNull: false }))}>
    <div>SF: {salesforceUsageDates(row)}</div>
    <div>WK: {dates.join(', ') || '-'}</div>
  </Box>;
}

export function AssetDiagnosticBadges({ row }: { row: AssetSourceRow }) {
  const { t } = useTranslation();
  const status = row.companyItemComparison?.status;
  return <Stack spacing={0.5}>
    {matchesAssetFilter(row, 'otherContract', new Date()) &&
      <Chip size="small" color="info" label={t('salesForceAssetReconciliation.otherContractBadge')} />}
    {status && <Typography variant="caption">
      {t(`salesForceAssetReconciliation.companyComparison.${status}`)}
    </Typography>}
  </Stack>;
}

function CompanyItemDetails({ row }: { row: AssetSourceRow }) {
  const { t } = useTranslation();
  const comparison = row.companyItemComparison;
  if (!comparison || (comparison.contracts.length <= 1 && comparison.status !== 'ComparisonBlocked')) return null;
  const text = (key: string) => t(`salesForceAssetReconciliation.${key}`);
  const discountDifferences = comparison.discountDifferences.filter((band) => band.quantityDifference !== 0);
  const showDiscounts = new Set(comparison.contracts.flatMap((contract) =>
    contract.discountGroups.map((band) => band.discountPercent))).size > 1 ||
    ['QuantityBalancedDiscountMismatch', 'DiscountMismatch', 'ComparisonBlocked'].includes(comparison.status);
  return <Box sx={{ overflowX: 'auto' }} data-testid="company-item-comparison">
    <Typography variant="subtitle2">{text('companyComparisonTitle')}</Typography>
    <Typography>{t(`salesForceAssetReconciliation.companyComparison.${comparison.status}`)}</Typography>
    <Typography>{text('netDifference')}: {comparison.quantityDifference ?? '-'}</Typography>
    <Table size="small">
      <TableHead><TableRow>{['contracts', 'sfQuantity', 'wkQuantity', 'netDifference', ...(showDiscounts ? ['discountAllocation'] : [])]
        .map((key) => <TableCell key={key}>{text(key)}</TableCell>)}</TableRow></TableHead>
      <TableBody>{comparison.contracts.map((contract, index) => <TableRow key={index}>
        <TableCell><AssetContractLinks row={{ ...contract, companyNumber: row.companyNumber }} /></TableCell>
        <TableCell>{contract.salesforceActiveQuantity ?? '-'}</TableCell>
        <TableCell>{contract.webkuaActiveQuantity ?? '-'}</TableCell>
        <TableCell>{contract.quantityDifference ?? '-'}</TableCell>
        {showDiscounts && <TableCell>{contract.discountGroups.map((band, bandIndex) => <div key={bandIndex}>
          {band.discountPercent == null ? text('unknownDiscount') : `${band.discountPercent}%`}:{' '}
          SF {band.salesforceQuantity ?? '-'} / WK {band.webkuaQuantity ?? '-'}
        </div>)}</TableCell>}
      </TableRow>)}</TableBody>
    </Table>
    {discountDifferences.length > 0 && <Box>
      <Typography variant="subtitle2">{text('netDiscountDifferences')}</Typography>
      {discountDifferences.map((band) => <div key={band.discountPercent}>
        {band.discountPercent}%: {band.quantityDifference}
      </div>)}
    </Box>}
  </Box>;
}

export function AssetReconciliationDetails({ data }: { data?: { source: AssetSourceRow } }) {
  const { t } = useTranslation();
  if (!data?.source) return null;
  const row = data.source;
  const assets = row.salesforceAssets ?? [];
  const items = row.webkuaItems ?? [];
  const latest = row.latestPendingOrder ?? (row.pendingOrders?.length === 1 ? row.pendingOrders[0] : null);
  const text = (key: string) => t(`salesForceAssetReconciliation.${key}`);
  const conclusionKey = row.kind === 'QuantityMismatch' || row.kind === 'ActivationMismatch' ? 'detailsQuantityMismatch'
    : row.kind === 'MissingInWebkua' ? 'detailsSfOnly'
    : row.kind === 'MissingInSalesforce' ? 'detailsWkOnly'
    : row.kind === 'UnmappedProduct' ? row.productMatchIssue ?? row.kind : row.kind;
  const showDiscounts = (row.discountGroups?.length ?? 0) > 1 ||
    ['DiscountMismatch', 'DiscountComparisonBlocked'].includes(row.kind) ||
    row.discountGroups?.some((band) => band.discountPercent == null ||
      band.discountPercent < 0 || band.discountPercent > 100 ||
      (band.salesforceQuantity ?? 0) < 0 || (band.webkuaQuantity ?? 0) < 0);
  const activity = (value: boolean | null) => value == null ? '-' : text(value ? 'sourceActive' : 'sourceInactive');
  const percent = (value: number | null) => value == null ? '-' : `${value}%`;
  const field = (key: string, value: ReactNode) => <Box>
    <Typography variant="subtitle2">{t(`salesForceAssetReconciliation.${key}`)}</Typography>{value}
  </Box>;
  return <ReconciliationDetailsLayout testId="asset-details" title={t(`salesForceAssetReconciliation.${conclusionKey}`, {
      defaultValue: row.kind, sfQuantity: row.salesforceActiveQuantity ?? '-', wkQuantity: row.webkuaActiveQuantity ?? '-',
    })}>
    {(row.comparisonIssues?.length ?? 0) > 0 && <Alert severity="warning">
      {row.comparisonIssues?.map((issue) => <div key={issue}>
        {t(`salesForceAssetReconciliation.${issue}`, { defaultValue: issue })}
      </div>)}
    </Alert>}
    {(row.salesforceAssets === undefined || row.webkuaItems === undefined) &&
      <Alert severity="warning">{text('sourceDetailsUnavailable')}</Alert>}
    {(row.pendingOrders?.length ?? 0) > 0 && field('hasPendingOrders', latest
      ? <Typography>{text('pendingQuantity')}: {latest.quantity} / {text('activationDate')}: {sourceDate(latest.activationDate)}</Typography>
      : <Alert severity="warning">{text('pendingUnknown')}</Alert>)}
    {(row.candidateSalesforceContractNumbers?.length ?? 0) > 0 &&
      field('candidateContracts', row.candidateSalesforceContractNumbers?.join(', '))}
    {matchesAssetFilter(row, 'otherContract', new Date()) && field('otherWebkuaContracts',
      <ReconciliationLinks links={(row.otherWebkuaContracts ?? []).map((contract) => ({
        label: t('salesForceAssetReconciliation.otherContractItem', {
          contractNumber: contract.contractNumber, quantity: contract.activeQuantity,
          licenseNumbers: contract.licenseNumbers.join(', '),
        }),
        href: row.companyNumber ? webkuaContractUrl(row.companyNumber, contract.contractNumber) : null,
      }))} />)}
    {(row.productCandidates?.length ?? 0) > 0 && <Box sx={{ overflowX: 'auto' }} data-testid="product-candidates">
      <Typography variant="subtitle2">{text('productCandidates')}</Typography>
      <Typography variant="caption">{text('candidateEvidenceOnly')}</Typography>
      <Table size="small">
        <TableHead><TableRow>{['item', 'wkQuantity', 'sourceLicense', 'discountAllocation']
          .map((key) => <TableCell key={key}>{text(key)}</TableCell>)}</TableRow></TableHead>
        <TableBody>{row.productCandidates?.map((candidate) => <TableRow key={`${candidate.itemId}-${candidate.itemType}`}>
          <TableCell>{candidate.itemId}<Typography variant="caption" component="div">
            {candidate.itemName} ({candidate.itemType})
          </Typography></TableCell>
          <TableCell>{candidate.webkuaActiveQuantity ?? '-'}</TableCell>
          <TableCell><ReconciliationLinks links={candidate.webkuaLicenseNumbers.map((number) => ({
            label: String(number), href: row.companyNumber ? webkuaProductListUrl(row.companyNumber) : null,
          }))} /></TableCell>
          <TableCell>{candidate.webkuaItems.map((source, index) => <div key={index}>
            {source.licenseNumber}: {source.activeQuantity ?? '-'} / {percent(source.discountPercent)}
          </div>)}</TableCell>
        </TableRow>)}</TableBody>
      </Table>
    </Box>}
    <CompanyItemDetails row={row} />
    <ReconciliationSourcePanels>
      <ReconciliationSourcePanel name="Salesforce" testId="sf-source-details">
        {assets.length === 0 ? <Typography>{row.salesforceAssets === undefined ? '-' : text(
          row.kind === 'MissingInSalesforce'
            ? 'noActiveSourceAssets' : 'noSourceAssets')}</Typography> : <Table size="small">
          <TableHead><TableRow>{['sourceAsset', 'sourceActiveQuantity', 'salesforceDiscount', 'sourceEndDate']
            .map((key) => <TableCell key={key}>{text(key)}</TableCell>)}</TableRow></TableHead>
          <TableBody>{assets.map((asset, index) => <TableRow key={`${asset.id}-${index}`}>
            <TableCell>
              <ReconciliationLink label={asset.name || asset.productCode || String(text('sourceAsset'))}
                href={asset.id ? salesforceRecordUrl('Asset', asset.id) : null} />
              {asset.name && asset.productCode && asset.name !== asset.productCode &&
                <Typography variant="caption" component="div">{asset.productCode}</Typography>}
              <Typography variant="caption" component="div">{asset.companyNumber || '-'}{' / '}
                <ReconciliationLink label={asset.contractNumber || String(text('contracts'))}
                  href={asset.contractId ? salesforceRecordUrl('Contract', asset.contractId) : null} />
              </Typography>
              {asset.status !== 'Active' && <Typography variant="caption" component="div">{asset.status || '-'}</Typography>}
              {asset.statusReason && <Typography variant="caption" component="div">{asset.statusReason}</Typography>}
              {asset.excludeFromRenewal === true && <Typography variant="caption" component="div">
                {text('excludedFromRenewal')}: {asset.renewalExclusionReason || '-'}
              </Typography>}
              {(asset.cancellationDate || asset.cancellationReason) && <Typography variant="caption" component="div">
                {text('cancellation')}: {sourceDate(asset.cancellationDate)} {asset.cancellationReason}
              </Typography>}
            </TableCell>
            <TableCell>{asset.activeQuantity ?? '-'}
              {asset.quantity != null && asset.quantity !== asset.activeQuantity &&
                <Typography variant="caption" component="div">{text('sourceQuantity')}: {asset.quantity}</Typography>}
              {asset.renewalQuantity != null &&
                <Typography variant="caption" component="div">{text('renewalQuantity')}: {asset.renewalQuantity}</Typography>}
            </TableCell>
            <TableCell>{percent(asset.discountPercent ?? 0)}</TableCell>
            <TableCell>{sourceDate(asset.usageEndDate)}</TableCell>
          </TableRow>)}</TableBody>
        </Table>}
      </ReconciliationSourcePanel>
      <ReconciliationSourcePanel name="WebKUA" testId="wk-source-details">
        {items.length === 0 ? <Typography>{row.webkuaItems === undefined ? '-' : text(
          row.productCandidates?.length || row.kind === 'UnmappedProduct' ? 'noMatchedSourceItems'
            : row.kind === 'MissingInWebkua'
              ? 'noActiveSourceItems' : 'noSourceItems')}</Typography> : <Table size="small">
          <TableHead><TableRow>{['webkuaLicenseNumbers', 'item', 'sourceActiveQuantity', 'sourceDiscount', 'sourceContractRenewal', 'usage']
            .map((key) => <TableCell key={key}>{text(key)}</TableCell>)}</TableRow></TableHead>
          <TableBody>{items.map((item, index) => <TableRow key={`${item.licenseId}-${item.itemLineId}-${index}`}>
            <TableCell><ReconciliationLink label={String(item.licenseNumber)}
              href={item.companyNumber ? webkuaProductListUrl(item.companyNumber) : null} /></TableCell>
            <TableCell>{item.itemId || '-'}<Typography variant="caption" component="div">
              <ReconciliationLink label={item.contractNumber || '-'}
                href={item.companyNumber && item.contractNumber ? webkuaContractUrl(item.companyNumber, item.contractNumber) : null} />
            </Typography>
              {!item.isActive && <Typography variant="caption" component="div">{activity(item.isActive)}</Typography>}
              {(item.licenseIsActive !== item.isActive || item.itemIsActive !== item.isActive) &&
                <Typography variant="caption" component="div">
                  {text('sourceLicense')}: {activity(item.licenseIsActive)} / {text('sourceItem')}: {activity(item.itemIsActive)}
                </Typography>}
            </TableCell>
            <TableCell>{item.activeQuantity}
              {item.quantity !== item.activeQuantity &&
                <Typography variant="caption" component="div">{text('sourceQuantity')}: {item.quantity}</Typography>}
            </TableCell>
            <TableCell>{percent(item.discountPercent)}</TableCell>
            <TableCell>{sourceDate(item.nextInvoicingDate)}
              {item.licenseTerminationDate &&
                <Typography variant="caption" component="div">{text('licenseTerminationDates')}: {sourceDate(item.licenseTerminationDate)}</Typography>}
              {item.maintenanceExpirationDate &&
                <Typography variant="caption" component="div">{text('maintenanceExpirationDates')}: {sourceDate(item.maintenanceExpirationDate)}</Typography>}
              {item.itemTerminationDate &&
                <Typography variant="caption" component="div">{text('itemTerminationDates')}: {sourceDate(item.itemTerminationDate)}</Typography>}
            </TableCell>
            <TableCell>{item.lastUsedDate2026 ? sourceDate(item.lastUsedDate2026) : text('noUsage2026')}
              {item.usedAfterRenewalDate === true && <Chip size="small" color="warning" label={text('afterRenewalBadge')} />}
              {item.lastUsedDate2026 && item.usedAfterRenewalDate == null &&
                <Typography variant="caption" component="div">{text('renewalUseUnknown')}</Typography>}
            </TableCell>
          </TableRow>)}</TableBody>
        </Table>}
      </ReconciliationSourcePanel>
    </ReconciliationSourcePanels>
    {showDiscounts && (row.discountGroups?.length ?? 0) > 0 && <Box sx={{ overflowX: 'auto' }} data-testid="discount-groups">
      <Typography variant="subtitle2">{text('discountAllocation')}</Typography>
      <Table size="small">
        <TableHead><TableRow>{['sourceDiscount', 'sfQuantity', 'wkQuantity']
          .map((key) => <TableCell key={key}>{text(key)}</TableCell>)}</TableRow></TableHead>
        <TableBody>{row.discountGroups?.map((group, index) => <TableRow key={index}>
          <TableCell>{group.discountPercent == null ? text('unknownDiscount') : `${group.discountPercent}%`}</TableCell>
          <TableCell>{group.salesforceQuantity ?? '-'}</TableCell>
          <TableCell>{group.webkuaQuantity ?? '-'}</TableCell>
        </TableRow>)}</TableBody>
      </Table>
    </Box>}
  </ReconciliationDetailsLayout>;
}
